import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { ExpenseCategoryDefinition } from '@/types'
import { NumericKeypad } from './NumericKeypad'

const NO_EXPENSE_CATEGORY = '__none__'

interface CashMovementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultType?: 'paid_in' | 'paid_out'
}

export function CashMovementDialog({
  open,
  onOpenChange,
  defaultType = 'paid_out',
}: CashMovementDialogProps) {
  const qc = useQueryClient()
  const [type, setType] = useState<'paid_in' | 'paid_out' | null>(null)
  const [amount, setAmount] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [expenseCategory, setExpenseCategory] = useState<string>('')
  const [showNotes, setShowNotes] = useState(false)

  const categoriesQuery = useQuery({
    queryKey: ['expense-category-definitions'],
    queryFn: () => apiClient.getExpenseCategoryDefinitions(),
    enabled: open && type === 'paid_out',
  })
  const categories = ((categoriesQuery.data?.data ?? []) as ExpenseCategoryDefinition[]).filter(
    (c) => c.is_active,
  )

  useEffect(() => {
    if (open) {
      // Start without a type picked so the cashier explicitly chooses.
      // The defaultType prop still wins if a caller wants to pre-select.
      setType(defaultType ?? null)
      setAmount('')
      setReason('')
      setNotes('')
      setExpenseCategory('')
      setShowNotes(false)
    }
  }, [open, defaultType])

  const mut = useMutation({
    mutationFn: () =>
      apiClient.addDayCashMovement({
        movement_type: type!,
        amount: Number(amount),
        reason: reason.trim(),
        notes: notes.trim() || undefined,
        expense_category:
          type === 'paid_out' && expenseCategory ? expenseCategory : undefined,
      }),
    onSuccess: () => {
      toastHelpers.success(type === 'paid_in' ? 'Money put in — saved' : 'Money taken out — saved')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['expenses'] })
      onOpenChange(false)
    },
    onError: (err: Error) => toastHelpers.apiError('Save cash drawer change', err),
  })

  const parsedAmount = Number(amount)
  const isValid =
    !!type &&
    amount.trim() !== '' &&
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    reason.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={(o) => !mut.isPending && onOpenChange(o)}>
      <DialogContent className="flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b px-6 py-4 pr-12">
          <DialogTitle>Add or take out money</DialogTitle>
          <DialogDescription>
            For cash going into or out of the drawer that wasn't a customer sale — for example,
            paying the milkman or topping up change.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setType('paid_in')}
              className={cn(
                'flex flex-col items-start gap-1.5 rounded-xl border-2 p-4 text-left transition',
                type === 'paid_in'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
                  : 'border-border bg-card hover:bg-muted/40',
              )}
            >
              <div className="flex items-center gap-2">
                <ArrowDownToLine className="h-5 w-5 text-emerald-600" />
                <span className="text-base font-semibold">Money put in</span>
              </div>
              <span className="text-sm text-muted-foreground">
                e.g. owner topped up the till
              </span>
            </button>
            <button
              type="button"
              onClick={() => setType('paid_out')}
              className={cn(
                'flex flex-col items-start gap-1.5 rounded-xl border-2 p-4 text-left transition',
                type === 'paid_out'
                  ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/40'
                  : 'border-border bg-card hover:bg-muted/40',
              )}
            >
              <div className="flex items-center gap-2">
                <ArrowUpFromLine className="h-5 w-5 text-rose-600" />
                <span className="text-base font-semibold">Money taken out</span>
              </div>
              <span className="text-sm text-muted-foreground">
                e.g. paid the milkman
              </span>
            </button>
          </div>

          {type && (
            <>
              <div className="space-y-2">
                <Label htmlFor="cash-movement-amount" className="text-base">
                  How much?
                </Label>
                <Input
                  id="cash-movement-amount"
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="h-14 text-3xl font-semibold tabular-nums"
                />
                <NumericKeypad value={amount} onChange={setAmount} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cash-movement-reason" className="text-base">
                  What for?
                </Label>
                <Input
                  id="cash-movement-reason"
                  placeholder={
                    type === 'paid_in'
                      ? 'e.g. owner topped up the till'
                      : 'e.g. paid milkman, bought ice'
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="h-12 text-base"
                />
              </div>

              {type === 'paid_out' && categories.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="cash-movement-expense-cat" className="text-base">
                    What kind of expense?{' '}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Select
                    value={expenseCategory || NO_EXPENSE_CATEGORY}
                    onValueChange={(value) =>
                      setExpenseCategory(value === NO_EXPENSE_CATEGORY ? '' : value)
                    }
                  >
                    <SelectTrigger id="cash-movement-expense-cat" className="h-12 text-base">
                      <SelectValue placeholder="Not an expense" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_EXPENSE_CATEGORY}>Not an expense</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.slug} value={cat.slug}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Pick a kind so this also shows up in your expense reports.
                  </p>
                </div>
              )}

              {showNotes ? (
                <div className="space-y-2">
                  <Label htmlFor="cash-movement-notes" className="text-base">
                    Note (optional)
                  </Label>
                  <Textarea
                    id="cash-movement-notes"
                    rows={2}
                    placeholder="Receipt number, vendor, anything you want to remember…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNotes(true)}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> Add a note (optional)
                </button>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => mut.mutate()}
            disabled={!isValid || mut.isPending}
          >
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
